import type { JSX, KeyboardEvent } from 'react';
import { styled, css } from 'styled-components';
import { useState, useRef } from 'react';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { Heading } from '../Heading';
import type { HeadingProps } from '../Heading';
import { variantStyleMap } from '../Heading/Heading';
import { Tooltip } from '../Tooltip';
import { Input } from '../Input';

const StyledInput = styled(Input)<{
  $height: string;
  $variant: NonNullable<HeadingProps['variant']>;
}>`
  &:not([rows]) {
    min-height: unset;
  }

  &:focus {
    height: ${({ $height }) => `${$height}px !important`};
  }

  && {
    ${({ $variant }) => variantStyleMap[$variant]}
    /* The input font styles (edit mode) needs the same font styles as Heading */
    --wui-input-font-size: var(--font-size);
    --wui-input-font-weight: var(--font-weight);
    --wui-input-line-height: var(--line-height);

    font-family: var(--font-family);
    width: 100%;
    padding: var(--wui-space-02);
    border: none;
    height: ${({ $height }) => `${$height}px`};
    min-height: ${({ $height }) => `${$height}px`};
    resize: none;
  }
`;

const editableStyles = css`
  &:has(+ :focus-within) {
    background: var(--wui-color-bg-surface-hover);
  }

  &:hover {
    background: var(--wui-color-bg-surface-hover);
    cursor: pointer;
  }
`;

const StyledHeading = styled(Heading)<{ $editingDisabled: boolean }>`
  width: 100%;
  border-radius: var(--wui-border-radius-02);
  padding: var(--wui-space-02);
  overflow-wrap: anywhere;
  transition: all var(--wui-motion-duration-01) var(--wui-motion-ease);
  ${({ $editingDisabled }) => !$editingDisabled && editableStyles}
`;

export type EditableHeadingProps = {
  /**
   * The initial text content of the heading.
   * This will be displayed and used as the starting value when editing begins.
   */
  children: string;

  /**
   * Callback fired when editing is completed (on blur or Enter key). Does not fire if the value is empty or the same as the previous value.
   * @param value The new text content after editing
   */
  onValueChange?: (value: string) => void;

  /**
   * Callback fired when the editing state changes.
   * @param isEditing True when entering edit mode, false when exiting
   */
  onEditingChange?: (isEditing: boolean) => void;

  /**
   * Custom tooltip text shown when hovering over the heading.
   */
  tooltipText?: string;

  /**
   * Sets an aria-label for the input and label for the screen reader only button.
   */
  ariaLabel?: string;

  /**
   * The text style to display. This will also affect what element (h1, h2, etc) is rendered, unless the `renderAs` prop is used
   */
  variant?: HeadingProps['variant'];

  /**
   * Force editing state for testing purposes
   * @ignore
   */
  __forceEditing?: boolean;

  /**
   * When true, prevents the heading from entering edit mode
   * @default false
   */
  editingDisabled?: boolean;
};

/**
 * A special heading component that allows for inline editing. When clicked or activated via keyboard,
 * it transforms into an editable text field. In its default state, it renders as a heading with
 * an accessible button that enables editing mode. The component handles empty values, keyboard
 * navigation (Enter to save, Escape to cancel), and provides callbacks for value and state changes.
 */
export const EditableHeading = ({
  children,
  onValueChange,
  onEditingChange,
  tooltipText = 'Click to edit title',
  ariaLabel = 'Edit title',
  variant = 'heading1',
  __forceEditing = false,
  editingDisabled = false,
}: EditableHeadingProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(children);
  const [previousValue, setPreviousValue] = useState(children);
  const [headingHeight, setHeadingHeight] = useState<string>('60');
  const headingRef = useRef<HTMLHeadingElement>(null);

  const handleSetEditing = (editing: boolean) => {
    if (editingDisabled) {
      return;
    }

    if (editing && headingRef.current) {
      setHeadingHeight(`${headingRef.current.offsetHeight}`);
    }

    if (editing) {
      setPreviousValue(value);
    }
    setIsEditing(editing);
    onEditingChange?.(editing);
  };

  const handleFinishEditing = () => {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      setValue(previousValue);
    } else if (trimmedValue !== previousValue && onValueChange) {
      onValueChange(trimmedValue);
    } else {
      setValue(trimmedValue);
    }
    handleSetEditing(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleFinishEditing();
    }
    if (event.key === 'Escape') {
      setValue(previousValue);
      handleSetEditing(false);
    }
  };

  const HeadingComponent = (
    <StyledHeading
      ref={headingRef}
      $editingDisabled={editingDisabled}
      onClick={() => handleSetEditing(true)}
      variant={variant}
    >
      {value}
    </StyledHeading>
  );

  if (editingDisabled) {
    return HeadingComponent;
  }

  if (isEditing || __forceEditing) {
    return (
      <StyledInput
        $height={headingHeight}
        $variant={variant}
        autoFocus={true}
        fullWidth={true}
        onBlur={handleFinishEditing}
        onChange={(event) => setValue(event.target.value)}
        onFocus={(event) => {
          const { length } = event.currentTarget.value;

          // place caret at end of input on focus
          event.currentTarget.setSelectionRange(length, length);
        }}
        onKeyDown={handleKeyDown}
        type="multiline"
        value={value}
      />
    );
  }

  return (
    <>
      <Tooltip content={tooltipText}>{HeadingComponent}</Tooltip>
      <ScreenReaderOnly>
        <button
          aria-label={ariaLabel}
          onClick={() => handleSetEditing(true)}
          type="button"
        />
      </ScreenReaderOnly>
    </>
  );
};
